// // src/context/CartContext.tsx
// //
// // Drop-in replacement for your existing CartContext.tsx — same import path,
// // same shape (items / addItem / addToCart / removeItem / removeFromCart /
// // clearCart / itemCount) already used across LandingHeader, ListingCard,
// // ListingDetailPage and ViewCartPage.
// //
// // Cart items now persist to localStorage automatically: every add/remove
// // is written straight through, and the cart survives page reloads and
// // browser restarts. Items only ever leave the cart when the user clicks
// // "Remove" on ViewCartPage (removeItem) — or a full "Clear cart", if you
// // wire one up later — never on their own.
// //
// // If your app has other places calling a different CartContext method
// // name (e.g. `updateQuantity`, `getTotal`), check the rest of your
// // codebase for `useCart()` usages before replacing your existing file
// // wholesale — this keeps the four method names already relied upon here,
// // but doesn't know about ones only used elsewhere.

// import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
// import { BrandListing } from "../types/brandlistingsmodel";

// const STORAGE_KEY = "adroj_cart_items_v1";

// interface CartContextValue {
//   items: BrandListing[];
//   addItem: (listing: BrandListing) => void;
//   addToCart: (listing: BrandListing) => void; // alias, same behavior as addItem
//   removeItem: (id: string) => void;
//   removeFromCart: (id: string) => void; // alias, same behavior as removeItem
//   clearCart: () => void;
//   itemCount: number;
// }

// const CartContext = createContext<CartContextValue | undefined>(undefined);

// function loadFromStorage(): BrandListing[] {
//   if (typeof window === "undefined") return [];
//   try {
//     const raw = window.localStorage.getItem(STORAGE_KEY);
//     if (!raw) return [];
//     const parsed = JSON.parse(raw);
//     return Array.isArray(parsed) ? parsed : [];
//   } catch {
//     // Corrupted or unreadable storage — start fresh rather than crash.
//     return [];
//   }
// }

// export function CartProvider({ children }: { children: ReactNode }) {
//   const [items, setItems] = useState<BrandListing[]>(() => loadFromStorage());

//   // Persist on every change. Also keeps multiple tabs roughly in sync via
//   // the browser's own storage event (not wired here, but the data is
//   // consistent if the user refreshes another tab).
//   useEffect(() => {
//     try {
//       window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
//     } catch {
//       // Storage full or unavailable (e.g. private browsing) — fail silently,
//       // the cart still works for the current session.
//     }
//   }, [items]);

//   const addItem = (listing: BrandListing) => {
//     setItems((prev) => (prev.some((l) => l.id === listing.id) ? prev : [...prev, listing]));
//   };

//   const removeItem = (id: string) => {
//     setItems((prev) => prev.filter((l) => l.id !== id));
//   };

//   const clearCart = () => setItems([]);

//   const value: CartContextValue = {
//     items,
//     addItem,
//     addToCart: addItem,
//     removeItem,
//     removeFromCart: removeItem,
//     clearCart,
//     itemCount: items.length,
//   };

//   return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
// }

// export function useCart(): CartContextValue {
//   const ctx = useContext(CartContext);
//   if (!ctx) throw new Error("useCart must be used within a CartProvider");
//   return ctx;
// }



//========================









// src/context/CartContext.tsx
//
// Restored to match the shape your existing internal cart flow already
// depends on (src/pages/brand/BrandPages.tsx — SpaceDetail, CartPage):
// items are { space: AdSpace, needsPrinting, needsInstallation, needsRemoval,
// printingCharge, installationCharge, creativeFile, installationNotes },
// with addToCart(space), removeFromCart(id), updateItem(id, updates),
// clearCart(), itemCount. That flow is untouched — no need to edit
// BrandPages.tsx at all.
//
// The public landing cart (ListingCard / ListingDetailPage / ViewCartPage)
// shares this same context — it wraps each BrandListing under `.space`
// too, so both the guest-facing flow and the authenticated /brand/cart
// flow read/write the same store without clashing shapes.
//
// New: cart contents persist to localStorage (key below), so they survive
// refreshes and closing the browser — items only leave via removeFromCart.

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { AdSpace } from "../types";

const STORAGE_KEY = "adroj_cart_items_v1";

export interface CartItem {
  space: AdSpace;
  needsPrinting?: boolean;
  needsInstallation?: boolean;
  needsRemoval?: boolean;
  printingCharge?: number;
  installationCharge?: number;
  creativeFile?: string;
  installationNotes?: string;
}

interface CartContextValue {
  items: CartItem[];
  addToCart: (space: AdSpace) => void;
  removeFromCart: (id: string) => void;
  updateItem: (id: string, updates: Partial<CartItem>) => void;
  clearCart: () => void;
  itemCount: number;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

function loadFromStorage(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => loadFromStorage());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Storage full or unavailable (e.g. private browsing) — fail silently.
    }
  }, [items]);

  const addToCart = (space: AdSpace) => {
    setItems((prev) => (prev.some((i) => i.space.id === space.id) ? prev : [...prev, { space }]));
  };

  const removeFromCart = (id: string) => {
    setItems((prev) => prev.filter((i) => i.space.id !== id));
  };

  const updateItem = (id: string, updates: Partial<CartItem>) => {
    setItems((prev) => prev.map((i) => (i.space.id === id ? { ...i, ...updates } : i)));
  };

  const clearCart = () => setItems([]);

  const value: CartContextValue = {
    items,
    addToCart,
    removeFromCart,
    updateItem,
    clearCart,
    itemCount: items.length,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}